import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNotify } from 'react-admin';
import { useTranslation } from 'react-i18next';
import { EditorialAssetSearchResultItem, RelatedAsset } from 'utility/types/dataProvider';
import {
  cleanupRelationships,
  transformToRelatedAsset,
  mergeRelationshipAssets,
  RelationshipType,
} from '../utils/relationshipUtils';
import { AssetFormValuesType } from 'screens/editorial/asset/context/AssetConfigUtilities';

/**
 * Hook to handle adding assets to relationship arrays from the modal.
 * Automatically cleans up assets from other relationships when adding to a new one.
 * @returns Function to add assets to a specific relationship
 */

type UseAddToRelationshipProps = {
  onClose?: () => void;
  silent?: boolean; // skips notify
};

// Function type returned by the hook
export type UseAddToRelationshipFnSig = (
  items: EditorialAssetSearchResultItem[],
  targetRelationship: RelationshipType,
) => RelatedAsset[] | undefined;

export function useAddToRelationship(
  props?: UseAddToRelationshipProps,
): UseAddToRelationshipFnSig {
  const { onClose, silent } = props || {};
  const notify = useNotify();
  const { getValues, setValue } = useFormContext<AssetFormValuesType>();
  const { t } = useTranslation();

  // Returns a helper function to add assets to a relationship type
  const addToRelationship = useCallback(
    (items: EditorialAssetSearchResultItem[], targetRelationship: RelationshipType) => {
      if (!items || items.length === 0) {
        console.warn('[useAddToRelationship] No items to add');
        return;
      }

      // Get current relationship arrays from form
      const currentChildren = getValues('children') || [];
      const currentParents = getValues('parents') || [];
      const currentSiblings = getValues('siblings') || [];

      // Transform items to RelatedAsset format
      const transformedItems = items.map(transformToRelatedAsset);

      // Clean up relationships - remove these assets from other relationship arrays
      const { cleanedChildren, cleanedParents, cleanedSiblings } = cleanupRelationships({
        targetRelationship: targetRelationship,
        assetsToAdd: transformedItems,
        currentChildren: currentChildren,
        currentParents: currentParents,
        currentSiblings: currentSiblings,
      });

      // Merge new items into the target relationship (avoiding duplicates)
      const targetAssets =
        targetRelationship === 'children'
          ? cleanedChildren
          : targetRelationship === 'parents'
          ? cleanedParents
          : cleanedSiblings;

      const updatedTargetAssets = mergeRelationshipAssets(targetAssets, transformedItems);

      // Count how many were actually added (not duplicates)
      const addedCount = updatedTargetAssets.length - targetAssets.length;

      // Update all three relationship arrays in the form
      setValue('children', cleanedChildren, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      setValue('parents', cleanedParents, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      setValue('siblings', cleanedSiblings, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });

      // Update the target relationship with merged assets
      setValue(targetRelationship, updatedTargetAssets, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });

      // Notify user (if not silent)
      if (!silent) {
        if (addedCount > 0) {
          const duplicatesCount = transformedItems.length - addedCount;
          if (duplicatesCount > 0) {
            notify(
              t(
                'blox.editorial.asset.related_content.notifications.added_with_duplicates',
                {
                  count: addedCount,
                  relationship: targetRelationship,
                  duplicates: duplicatesCount,
                },
              ),
              { type: 'success' },
            );
          } else {
            notify(
              t('blox.editorial.asset.related_content.notifications.added', {
                count: addedCount,
                relationship: targetRelationship,
              }),
              { type: 'success' },
            );
          }
        } else {
          notify(
            t('blox.editorial.asset.related_content.notifications.all_duplicates', {
              relationship: targetRelationship,
            }),
            { type: 'info' },
          );
        }
      }

      // Close modal if callback provided
      if (typeof onClose === 'function') {
        onClose();
      }

      // Return latest updated array to caller to use as needed
      const result = getValues(targetRelationship) || [];
      return result;
    },
    [getValues, notify, onClose, setValue, silent, t],
  );

  return addToRelationship;
}
